/**
 * O tema da SPA: o que o aluno escolheu, o que o sistema diz e o que a tela aplica.
 *
 * Tres valores, nao dois:
 *
 *   'light' | 'dark'  escolha explicita do aluno — mora no PERFIL (`users.theme_preference`)
 *   null              ele nunca escolheu; a tela segue o `prefers-color-scheme` do sistema
 *
 * O nulo e o estado normal de quem acabou de entrar, e por isso ele atravessa o backend
 * inteiro sem virar default em lugar nenhum: um default no banco ou no serializer faria a
 * preferencia do sistema nunca valer.
 *
 * `applyTheme` mexe em DUAS coisas na raiz do documento:
 *   - a classe `.dark`, que e o gatilho de todas as utilidades `dark:` (ver o
 *     `@custom-variant` em index.css — sem ele o Tailwind v4 ignora a classe);
 *   - `color-scheme`, que governa o que o navegador desenha por conta propria: barra de
 *     rolagem, campos nativos, `<select>` aberto. Sem isso a tela clara fica com scrollbar
 *     preta, e o defeito parece "meio aplicado".
 */

export type ThemePreference = 'light' | 'dark' | null
export type ResolvedTheme = 'light' | 'dark'

/** Cache local, so para nao piscar o tema errado antes de o perfil chegar. */
export const THEME_CACHE_KEY = 'medhunter-theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference ?? systemTheme()
}

export function applyTheme(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

/** `true` so para os dois valores que o backend aceita; qualquer outra coisa vira sistema. */
export function isThemePreference(value: unknown): value is Exclude<ThemePreference, null> {
  return value === 'light' || value === 'dark'
}

export function readCachedPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_CACHE_KEY)
    return isThemePreference(stored) ? stored : null
  } catch {
    // Navegador com storage bloqueado: seguir o sistema e a resposta certa, nao um erro.
    return null
  }
}

export function writeCachedPreference(preference: ThemePreference) {
  try {
    if (preference) localStorage.setItem(THEME_CACHE_KEY, preference)
    else localStorage.removeItem(THEME_CACHE_KEY)
  } catch {
    // Idem: o perfil continua sendo a fonte da verdade; o cache e so anti-piscada.
  }
}

/**
 * Avisa quando o sistema troca de modo. Quem escuta so age enquanto a preferencia e nula —
 * ninguem que escolheu "claro" quer a tela virando sozinha as 18h.
 */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}

  const query = window.matchMedia(DARK_QUERY)
  const listener = (event: MediaQueryListEvent) => onChange(event.matches ? 'dark' : 'light')
  query.addEventListener('change', listener)

  return () => query.removeEventListener('change', listener)
}
